import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationRef, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { ActivityRegistryModule } from './activity-registry/activity-registry.module';
import { BillingHistoryComponent } from './billing-history/billing-history.component';
import { BillingHistoryModule } from './billing-history/billing-history.module';
import { AskTeacherButtonComponent } from './contact-us/ask-teacher-button/ask-teacher-button.component';
import { ContactUsFaqsComponent } from './contact-us/contact-us-page-faqs/contact-us-faqs.component';
import { ContactUsPictureComponent } from './contact-us/contact-us-picture/contact-us-picture.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { ContactUsModule } from './contact-us/contact-us.module';
import { CoreModule } from './core/core.module';
import { EndOfLevelExamComponent, EndOfLevelExamModule } from './exams/end-of-level-exam/end-of-level-exam.component';
import { FaqPageComponent } from './faq/faq-page/faq-page.component';
import { FaqModule } from './faq/faq.module';
import { FooterComponent } from './footer/footer.component';
import { FooterModule } from './footer/footer.module';
import { GameRulesComponent } from './game-rules/game-rules.component';
import { GameRulesModule } from './game-rules/game-rules.module';
import { HeaderComponent } from './header/header.component';
import { HeaderModule } from './header/header.module';
import { HomeModule } from './home/home.module';
import { StudyBundleComponent } from './home/study-bundle/study-bundle.component';
import { PreviousLevelsPageComponent } from './industry-content/previous-levels-page/previous-levels-page.component';
import { PreviousLevelsModule } from './industry-content/previous-levels-page/previous-levels.module';
import { LearningModule } from './learning/learning.module';
import { LessonPageComponent } from './learning/lesson-page/lesson-page.component';
import { LoginPageComponent } from './login/login-page/login-page.component';
import { LoginModule } from './login/login.module';
import { PasswordSetupPageComponent } from './login/password-setup-page/password-setup-page.component';
import { RecoveryPageComponent } from './login/recovery-page/recovery-page.component';
import { MidLevelExamPageComponent } from './mid-level-exam/mid-level-exam-page/mid-level-exam-page.component';
import { MidLevelExamModule } from './mid-level-exam/mid-level-exam.module';
import { MyCoachPageComponent, MyCoachPageModule } from './my-coach/my-coach-page.component';
import { NoCacheInterceptor } from './no-cache-interceptor';
import { NotificationsListPageComponent } from './notifications-list/notifications-list-page/notifications-list-page.component';
import { NotificationsListModule } from './notifications-list/notifications-list.module';
import { OverlayModule } from './overlay/overlay.module';
import { PlacementQuizPageComponent } from './placement-quiz/placement-quiz-page/placement-quiz-page.component';
import { PlacementQuizModule } from './placement-quiz/placement-quiz.module';
import { PlatformTourPageComponent } from './platform-tour/platform-tour-page/platform-tour-page.component';
import { PlatformTourModule } from './platform-tour/platform-tour.module';
import { PreferencesComponent } from './preferences/preferences.component';
import { PreferencesModule } from './preferences/preferences.module';
import { PrivateClassesPageComponent } from './private-classes/private-classes-page/private-classes-page.component';
import { PrivateClassesModule } from './private-classes/private-classes.module';
import { ProgressPageComponent } from './progress/progress-page/progress-page.component';
import { ProgressModule } from './progress/progress.module';
import { ReferralsPageComponent } from './referrals/referrals-page/referrals-page.component';
import { ReferralsModule } from './referrals/referrals.module';
import { AskTeacherComponent } from './shared/ask-teacher/ask-teacher.component';
import { AuthInterceptor } from './shared/auth-interceptor';
import { NotificationService } from './shared/notification.service';
import { TitleBarComponent } from './shared/title-bar/title-bar.component';
import { WritingFeedbackRequestButtonComponent } from './shared/writing-feedback-request-button/writing-feedback-request-button.component';
import { TeacherFeedbackModule } from './teacher-feedback/teacher-feedback.module';
import { UnitIndexPageComponent } from './unit-index/unit-index-page/unit-index-page.component';
import { UnitIndexModule } from './unit-index/unit-index.module';
import { AccountSharingModule } from './account-sharing/account-sharing.module';
import { AccountSharingPageComponent } from './account-sharing/account-sharing-page/account-sharing-page.component';
import { BaseNotificationModalModule } from './shared/base-notification-modal/base-notification-modal.component';
import { ModalService } from './shared/modal.service';
import { SharedAccountService } from './login/shared-account.service';
import { ChatBotEventService } from './shared/chat-bot-event.service';
import { HybridClassesPageComponent } from './hybrid-classes/hybrid-classes-page/hybrid-classes-page.component';
import { HybridClassesModule } from './hybrid-classes/hybrid-classes.module';

@NgModule({
  entryComponents: [
    BillingHistoryComponent,
    PreferencesComponent,
    FooterComponent,
    HeaderComponent,
    StudyBundleComponent,
    LoginPageComponent,
    PasswordSetupPageComponent,
    RecoveryPageComponent,
    ProgressPageComponent,
    TitleBarComponent,
    WritingFeedbackRequestButtonComponent,
    PreviousLevelsPageComponent,
    LessonPageComponent,
    GameRulesComponent,
    FaqPageComponent,
    ContactUsComponent,
    ContactUsPictureComponent,
    ContactUsFaqsComponent,
    AskTeacherButtonComponent,
    AskTeacherComponent,
    UnitIndexPageComponent,
    PlacementQuizPageComponent,
    MidLevelExamPageComponent,
    PrivateClassesPageComponent,
    ReferralsPageComponent,
    PlatformTourPageComponent,
    NotificationsListPageComponent,
  ],
  imports: [
    ActivityRegistryModule,
    BillingHistoryModule,
    BrowserAnimationsModule,
    BrowserModule,
    StoreModule.forRoot({}, {}),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    FooterModule,
    HeaderModule,
    HomeModule,
    LearningModule,
    LoginModule,
    OverlayModule,
    ProgressModule,
    EndOfLevelExamModule,
    PreferencesModule,
    TeacherFeedbackModule,
    CoreModule,
    PreviousLevelsModule,
    GameRulesModule,
    FaqModule,
    ContactUsModule,
    UnitIndexModule,
    MidLevelExamModule,
    PrivateClassesModule,
    PlacementQuizModule,
    ReferralsModule,
    PlatformTourModule,
    MyCoachPageModule,
    NotificationsListModule,
    BaseNotificationModalModule,
    HybridClassesModule,
    AccountSharingModule,
    NgbModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: NoCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    ModalService,
    NotificationService,
    SharedAccountService,
    ChatBotEventService,
    {
      provide: APP_INITIALIZER,
      useFactory: (
        modalService: ModalService,
        sharedAccountService: SharedAccountService,
        notificationService: NotificationService,
        chatBotEventService: ChatBotEventService) => {
          return () => {
            return Promise.all([
              modalService.init(),
              sharedAccountService.init(),
              notificationService.init(),
              chatBotEventService.init()
            ]);
          }
      },
      deps: [ModalService, SharedAccountService, NotificationService, ChatBotEventService],
      multi: true,
    },
  ],
})
export class AppModule {
  ngDoBootstrap(appRef: ApplicationRef) {
    const componentsToBootstrap = {
      'lp2-billing-history': BillingHistoryComponent,
      'lp2-preferences': PreferencesComponent,
      'lp2-footer': FooterComponent,
      'lp2-header': HeaderComponent,
      'lp2-login-page': LoginPageComponent,
      'lp2-password-setup-page': PasswordSetupPageComponent,
      'lp2-progress-page': ProgressPageComponent,
      'lp2-end-of-level-exam': EndOfLevelExamComponent,
      'lp2-my-coah-page': MyCoachPageComponent,
      'lp2-recovery-page': RecoveryPageComponent,
      'lp2-study-bundle': StudyBundleComponent,
      'lp2-title-bar': TitleBarComponent,
      'lp2-writing-feedback-request-button': WritingFeedbackRequestButtonComponent,
      'lp2-industry-content-previous-levels-page': PreviousLevelsPageComponent,
      'lp2-lesson-page': LessonPageComponent,
      'lp2-game-rules': GameRulesComponent,
      'lp2-contact-us': ContactUsComponent,
      'lp2-faq-page': FaqPageComponent,
      'lp2-contact-us-picture': ContactUsPictureComponent,
      'lp2-contact-us-faqs': ContactUsFaqsComponent,
      'lp2-ask-teacher-button': AskTeacherButtonComponent,
      'lp2-ask-teacher': AskTeacherComponent,
      'lp2-unit-index-page': UnitIndexPageComponent,
      'lp2-private-classes-page': PrivateClassesPageComponent,
      'lp2-referrals-page': ReferralsPageComponent,
      'lp2-platform-tour-page': PlatformTourPageComponent,
      'lp2-placement-quiz-page':PlacementQuizPageComponent,
      'lp2-mid-level-exam-page':MidLevelExamPageComponent,
      'lp2-notifications-list-page': NotificationsListPageComponent,
      'lp2-hybrid-classes-page': HybridClassesPageComponent,
      'lp2-account-sharing-page': AccountSharingPageComponent,
    };

    for (const selector in componentsToBootstrap) {
      if (componentsToBootstrap.hasOwnProperty(selector)) {
        const elements = document.querySelectorAll(selector);
        Array.from(elements).forEach((element) => {
          appRef.bootstrap(componentsToBootstrap[selector], element);
        });
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/app.module.ts